import { useEffect, useMemo } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { PlusCircle } from "lucide-react";
import { InstaPassLogo } from "../Components/layout/Components/InstaPassLogo";
import OrganizerSidebar from "../Components/CreateEvent/OrganizerSidebar";
import OrganizerMobileNavDropdown from "../Components/CreateEvent/OrganizerMobileNavDropdown";
import OrganizerDashboardOverview from "./pages/OrganizerDashboardOverview";
import UserEvents from "./pages/UserEvents";
import UserRevenue from "./pages/UserRevenue";
import UserProfile from "./pages/UserProfile";
import { GetUserOwnEvents } from "../redux/actions/Events";

const TAB_TITLES = {
  overview: "Dashboard",
  events: "My Events",
  revenue: "Sales & Revenue",
  profile: "Settings",
};

function OrganizerDashboardHeader({ tab }) {
  const user = useSelector((state) => state.user.user);
  const displayName = [user?.firstName, user?.lastName].filter(Boolean).join(" ") || user?.email || "Organizer";

  return (
    <header className="shrink-0 h-[64px] border-b border-[#1E293B]/60 bg-[#060D1B]/95 backdrop-blur-lg flex items-center justify-between px-4 sm:px-6 z-30">
      <div className="min-w-0">
        <h1 className="text-[16px] text-white truncate" style={{ fontWeight: 700 }}>
          {TAB_TITLES[tab] || "Dashboard"}
        </h1>
        <p className="text-[11px] text-white/35 truncate">{displayName}</p>
      </div>
      <Link
        to="/create-event/form"
        className="inline-flex items-center gap-2 px-3.5 py-2 rounded-xl bg-blue-500 hover:bg-blue-600 text-white text-[12px] transition-colors shrink-0"
        style={{ fontWeight: 600 }}
      >
        <PlusCircle className="w-3.5 h-3.5" />
        <span className="hidden sm:inline">Create Event</span>
      </Link>
    </header>
  );
}

function MobileDashboardHeader() {
  return (
    <header className="lg:hidden fixed top-0 left-0 right-0 h-14 border-b border-[#1E293B]/60 bg-[#060D1B]/95 backdrop-blur-lg grid grid-cols-[1fr_auto_1fr] items-center gap-2 px-3 z-30">
      <OrganizerMobileNavDropdown className="min-w-0 justify-self-start" />
      <Link to="/" className="justify-self-center shrink-0" aria-label="InstaPass home">
        <InstaPassLogo size="sm" />
      </Link>
      <Link
        to="/create-event/form"
        className="flex h-10 w-10 items-center justify-center rounded-xl bg-blue-500 hover:bg-blue-600 text-white transition-colors shrink-0 justify-self-end"
        aria-label="Create event"
      >
        <PlusCircle className="w-4 h-4" />
      </Link>
    </header>
  );
}

function UserDashboardIndex() {
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();

  const tab = useMemo(() => {
    const value = searchParams.get("tab") || "overview";
    if (["overview", "events", "revenue", "profile"].includes(value)) return value;
    return "overview";
  }, [searchParams]);

  useEffect(() => {
    dispatch(GetUserOwnEvents());
  }, [dispatch]);

  const navigateTab = (nextTab) => {
    if (nextTab === "overview") {
      setSearchParams({});
    } else {
      setSearchParams({ tab: nextTab });
    }
  };

  const renderTab = () => {
    switch (tab) {
      case "events":
        return <UserEvents />;
      case "revenue":
        return <UserRevenue />;
      case "profile":
        return (
          <div className="flex-1 min-h-0 overflow-y-auto p-4 sm:p-6">
            <div className="bg-white rounded-xl shadow-sm min-h-[400px]">
              <UserProfile />
            </div>
          </div>
        );
      default:
        return (
          <OrganizerDashboardOverview onNavigateTab={navigateTab} />
        );
    }
  };

  return (
    <div
      className="min-h-screen bg-[#060D1B] overflow-x-hidden"
      style={{ fontFamily: "'Outfit', 'Inter', sans-serif" }}
    >
      <OrganizerSidebar />
      <MobileDashboardHeader />

      <div className="ml-0 lg:ml-[260px] pt-14 lg:pt-0 flex flex-col min-h-screen min-w-0">
        {tab === "profile" && (
          <div className="hidden lg:block">
            <OrganizerDashboardHeader tab={tab} />
          </div>
        )}
        <main className="flex flex-col flex-1 min-h-0 overflow-hidden">{renderTab()}</main>
      </div>
    </div>
  );
}

export default UserDashboardIndex;
